import React, { useEffect, useRef, useState } from "react";
import Header from "../../Common/header";
import Footer from "../../Common/footer";
import Select from "react-select";
import SimpleReactValidator from "simple-react-validator";
import "react-datepicker/dist/react-datepicker.css";
import { ReactTags } from "react-tag-autocomplete";
import "leaflet/dist/leaflet.css";
import { MapContainer } from "react-leaflet";
import AreaSelect from "../DataVisualization/GeneralMaps/AreaSelect";
import "leaflet-area-select";
import StepsNavbar from "./Components/stepsNavbar";
import { keyOptions as keywordSuggestions } from "./InternalFiles/keywordSuggestions";

//Value of Status dropdown
const options = [
  { value: 0, label: "Planned" },
  { value: 1, label: "In progress" },
  { value: 2, label: "Complete" },
];

export default function ProjectDescription(props) {
  const projectData = props.projectData;
  const setProjectData = props.setProjectData;
  const projectDataRef = useRef(projectData);
  const [, forceUpdate] = useState();
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [updateBounds, setUpdateBounds] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [vesselSuggestions, setVesselSuggestions] = useState([]);
  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: this,
      validators: {
        latitude: {
          // name the rule
          message:
            "The :attribute must be valid range of in -90 and +90 degrees",
          rule: (val, params, validator) => {
            return validator.helpers.testRegex(
              val,
              /^(\+|-)?(?:90(?:(?:\.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,6})?))$/i
            );
          },
          required: true, // optional
        },

        longitude: {
          // name the rule
          message:
            "The :attribute must be valid range of in -180 and +180 degrees",
          rule: (val, params, validator) => {
            return validator.helpers.testRegex(
              val,
              /^(\+|-)?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,6})?))$/i
            );
          },
          required: true, // optional
        },

        endDateRequiredUnlessInProgress: {
          message:
            "Project End Date is required unless status is In progress.",
          rule: (val, params, validator) => {
            const status =
              projectDataRef.current &&
              projectDataRef.current.selectedOption
                ? projectDataRef.current.selectedOption.value
                : null;
            if (status === 1) {
              return true;
            }
            return validator.helpers.testRegex(val, /.+/);
          },
          required: true,
        },

        URL: {
          message:
            "The :attribute must be a valid URL (example: https://example.com)",
          rule: (val, params, validator) => {
            return validator.helpers.testRegex(
              val,
              /^(https?:\/\/)(?:[\w-]+\.)+[\w-]{2,}(?:\/[\w\-./?%&=]*)?$/i
            );
          },
          required: false,
        },
      },
    })
  );

  useEffect(() => {
    projectDataRef.current = projectData;
  }, [projectData]);

  /**
   * Checks if all fields are valid. If all fields are valid, it calls the nextPage() function to move to the next page.
   * If there are invalid fields, it displays the validation messages for the invalid fields and scrolls to the top.
   * @param {*} e Event object
   */
  function next(e) {
    if (simpleValidator.current.allValid()) {
      props.nextPage();
    } else {
      simpleValidator.current.showMessages();
      window.scrollTo(0, 0);
      // rerender to show messages for the first time
      forceUpdate(1);
    }
  }

  useEffect(() => {
    if (
      props.projectData.coordinates.eastLongitude !== "" &&
      props.projectData.coordinates.northLatitude !== ""
    ) {
      setShowCoordinates(true);
    } else {
      setShowCoordinates(false);
    }
  }, [props.projectData.coordinates]);

  function getAttributesByName(attributeName) {
    let attributeChoices = [];
    try {
      const attributesStr = localStorage.getItem("attributes");
      if (attributesStr) {
        const attributesData = JSON.parse(attributesStr);
        if (attributesData && attributesData.filterOptions && Array.isArray(attributesData.filterOptions)) {
          attributeChoices = attributesData.filterOptions.filter((filterOption) => filterOption.category === attributeName);
          attributeChoices = attributeChoices.filter((value, index, self) => self.findIndex((value2) => value2.name.toLowerCase() === value.name.toLowerCase()) === index);
        }
      }
    } catch (e) {
      console.error('Error parsing attributes from localStorage:', e);
    }
    return attributeChoices;
  }

  useEffect(() => {
    const loadVesselSuggestions = () => {
      const vesselChoices = getAttributesByName("vessel");
      const formattedSuggestions = vesselChoices
        .filter((vessel) => vessel && vessel.name)
        .map((vessel) => ({
          value: vessel.name.trim(),
          label: vessel.name.trim()
        }));
      setVesselSuggestions(formattedSuggestions);
    };

    loadVesselSuggestions();

    const handleAttributesUpdated = () => {
      loadVesselSuggestions();
    };

    window.addEventListener('localStorage:attributesUpdated', handleAttributesUpdated);
    return () => {
      window.removeEventListener('localStorage:attributesUpdated', handleAttributesUpdated);
    };
  }, []);

  /**
   * Handles changes in the input fields of the form. The function updates the state object properties based on the input field that was passed into the change event.
   * @param {*} e Event object
   * @param {*} input Name of state property to update
   */
  function handleChange(e, input) {
    setProjectData((prevState) => ({
      ...prevState,
      [input]:
        typeof e.target.value === "string"
          ? e.target.value.trim()
          : e.target.value,
    }));

    // Without this 'if' statement, if you add a website and then remove it, it just stays in the validation object and keeps trying to validate it unless its deleted.
    if (input === "website" && e.target.value === "") {
      const newValidators = { ...simpleValidator.current.fields };
      delete newValidators["Project Website"];
      simpleValidator.current.fields = newValidators;
    }
  }

  /* Project Description chips handling start ################################################################## */
  function onKeywordTagDelete(item) {
    const keywordChips = projectData.keywordChips.slice(0);
    keywordChips.splice(item, 1);
    setProjectData((prevState) => ({
      ...prevState,
      keywordChips: keywordChips,
    }));

    let keyword = projectData.keyword.slice(0);
    keyword.splice(item, 1);
    setProjectData((prevState) => ({
      ...prevState,
      keyword: keyword,
    }));
  }

  function onKeywordTagAddition(item) {
    const keyword = [].concat(projectData.keyword, [item.value]);
    setProjectData((prevState) => ({
      ...prevState,
      keyword: keyword,
    }));

    const keywordChips = [].concat(projectData.keywordChips, item);
    setProjectData((prevState) => ({
      ...prevState,
      keywordChips: keywordChips,
    }));
  }

  function onVesselTagDelete(item) {
    const vesselChips = projectData.vesselChips.slice(0);
    vesselChips.splice(item, 1);
    setProjectData((prevState) => ({
      ...prevState,
      vesselChips: vesselChips,
    }));

    const vessels = projectData.vessels.slice(0);
    vessels.splice(item, 1);
    setProjectData((prevState) => ({
      ...prevState,
      vessels: vessels,
    }));
  }

  function onVesselTagAddition(item) {
    const vessels = [].concat(projectData.vessels, [item.value]);
    setProjectData((prevState) => ({
      ...prevState,
      vessels: vessels,
    }));

    const vesselChips = [].concat(projectData.vesselChips, item);
    setProjectData((prevState) => ({
      ...prevState,
      vesselChips: vesselChips,
    }));
  }
  /* Project Description chips handling end ################################################################## */

  /* Map component functions start ####################################################### */
  function onClickResetDirection(e) {
    e.preventDefault();
    setProjectData((prevState) => ({
      ...prevState,
      bounds: {
        north: "",
        south: "",
        east: "",
        west: "",
      },
      showCoordinates: false,
    }));
    setShowCoordinates(false);
    setIsSelecting(false);
  }

  function calculateCenter(north, south, east, west) {
    if (east < 0) east += 360;
    if (west < 0) west += 360;

    // Calculate the average of the two longitudes
    let centerLongitude = (east + west) / 2;

    // Ensure the result is within the standard range (-180 to +180 degrees)
    if (centerLongitude < -180) centerLongitude += 360;
    if (centerLongitude > 180) centerLongitude -= 360;

    let latitude = (north + south) / 2;

    return [latitude, centerLongitude];
  }

  function handleCoordinateChange(bounds) {
    let north = bounds.north;
    let east = bounds.east;
    let south = bounds.south;
    let west = bounds.west;
    bounds = {
      north: north,
      south: south,
      east: east,
      west: west,
    };

    //Get the center point of the bounds.
    let centerPoint = calculateCenter(
      parseFloat(north),
      parseFloat(south),
      parseFloat(east),
      parseFloat(west)
    );

    setProjectData((prevState) => ({
      ...prevState,
      bounds: bounds,
      centerPoint: centerPoint,
    }));
  }

  function handleBoundsUpdate(bounds) {
    if (!bounds || bounds.east === undefined || bounds.north === undefined) {
      return;
    }

    handleCoordinateChange({
      north: parseFloat(bounds.north).toFixed(6),
      south: parseFloat(bounds.south).toFixed(6),
      east: parseFloat(bounds.east).toFixed(6),
      west: parseFloat(bounds.west).toFixed(6),
    });
  }

  function onClickShowCoordinates() {
    setProjectData((prevState) => ({
      ...prevState,
      showCoordinates: true,
    }));
  }
  /* Map component functions end ####################################################### */

  // Functions to handle Bathymetry data
  function handleBackscatterChange(e) {
    const { name, checked } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  }

  function handleAnalyzedForNAVWARNChange(e) {
    const { name, checked } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  }

  function handleSoundingChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleTechniqueOfSoundingChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleResolutionChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleDataAcquisitionSoftwareVersionChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleDataProcessingSoftwareVersionChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleHorizontalCoordinatesSystemChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleHorizontalDatumChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleProjectionChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handlePositioningMethodChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handlePositioningHardwareChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleBenchmarkReferenceChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleBenchmarkReferenceChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleBenchmarkCoordinatesChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleVerticalReferenceSystemChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleBenchmarkReferenceHeightChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleWaterLevelReductionMethodFileAppliedChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleTidalStationReferenceVerticalReductionModelChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleHorizontalAccuracyChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleVerticalAccuracyChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleVerticalAccuracyChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleSoundingCorrectedForVesselDraftChange(e) {
    const { name, checked } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  }

  function handleCalibrationDataChange(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: typeof value === 'string' ? value.trim() : value,
    }));
  }

  function handle_IHO_CATZOC_Change(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handle_IHOOrderOfSurvey_Change(e) {
    const { name, value } = e.target;
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

/* Bathymetry functions end ####################################################### */

  return (
    <>
      <Header />

      <StepsNavbar
        currentStep={props.step}
        setStep={props.setStep}
        next={next}
      />
      <div className="overflow-auto dark:bg-gray-900">
        {/* Form */}
        <form className="max-w-2xl my-8 sm:my-12 m-auto bg-white dark:bg-gray-800 dark:border-gray-700">
          <div className="bg-gray-100 dark:bg-gray-800 border dark:border-gray-700">
            <div>
              <div>
                <h2 className="text-center font-bold text-gray-900 dark:text-gray-100 sm:text-xl sm:tracking-tight lg:text-3xl py-6 border-b-2 dark:bg-gray-800 dark:border-gray-600">
                  Project Description
                </h2>
              </div>

              <div className="mt-6 mb-10 grid gap-y-4 gap-x-6 sm:grid-cols-6 px-8 sm:px-14 dark:bg-gray-800">
                {/*  Project Title section ------------------------------------------------------------- */}
                <div className="sm:col-span-6 dark:bg-gray-800">
                  <label
                    htmlFor="title"
                    className="block text-base font-semibold text-gray-900 dark:text-white"
                  >
                    Project Title
                    <span className="text-red-700 font-bold border-red-200">
                      *
                    </span>
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Great slave lake survey data"
                      onChange={(e) => handleChange(e, "title")}
                      onBlur={() => {
                        simpleValidator.current.showMessageFor("title");
                      }}
                      defaultValue={projectData.title}
                      type="text"
                      name="title"
                      id="title"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                      required
                    />
                    {simpleValidator.current.message(
                      "Project Title",
                      projectData.title,
                      "required|string",
                      {
                        className: "text-red-600",
                      }
                    )}
                  </div>
                </div>

                {/*  Abstract section ------------------------------------------------------------- */}
                <div className="sm:col-span-6">
                  <label
                    htmlFor="abstract"
                    className="block text-base font-semibold text-gray-900 dark:text-white"
                  >
                    Abstract
                    <span className="text-red-700 font-bold border-red-200">
                      *
                    </span>
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="abstract"
                      rows="3"
                      placeholder="A brief description"
                      onChange={(e) => handleChange(e, "abstract")}
                      defaultValue={projectData.abstract}
                      name="abstract"
                      className="py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 border border-gray-300 rounded-md dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                    {simpleValidator.current.message(
                      "Abstract",
                      projectData.abstract,
                      "required",
                      {
                        className: "text-red-600",
                      }
                    )}
                  </div>
                </div>

                {/*   Research Program Name section ------------------------------------------------------------- */}
                <div className="sm:col-span-6">
                  <label
                    htmlFor="research-name"
                    className="block text-base font-semibold text-gray-900  dark:text-white"
                  >
                    Research Program Name
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Research program name"
                      onChange={(e) => handleChange(e, "researchName")}
                      defaultValue={projectData.researchName}
                      type="text"
                      name="research-name"
                      id="research-name"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                    {projectData.researchName === "" ? (
                      <></>
                    ) : (
                      simpleValidator.current.message(
                        "Research program name",
                        projectData.researchName,
                        "required|string",
                        {
                          className: "text-red-600",
                        }
                      )
                    )}
                  </div>
                </div>

                {/*   Project Website section ------------------------------------------------------------- */}
                <div className="sm:col-span-6">
                  <label
                    htmlFor="website"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Project Website
                  </label>
                  <div className="mt-1">
                    <input
                      onChange={(e) => handleChange(e, "website")}
                      defaultValue={projectData.website}
                      type="text"
                      name="website"
                      id="website"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                    {projectData.website === "" ? (
                      <></>
                    ) : (
                      simpleValidator.current.message(
                        "Project Website",
                        projectData.website,
                        "URL",
                        {
                          className: "text-red-600",
                        }
                      )
                    )}
                  </div>
                </div>

                {/*   Project Keywords section ------------------------------------------------------------- */}
                <div className="sm:col-span-6">
                  <label
                    htmlFor="keywords"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Project Keywords
                    <span className="text-red-700 font-bold border-red-200">
                      *
                    </span>
                  </label>
                  <div className="mt-1">
                    <div
                      data-cy="keywords"
                      className="block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md text-gray-800 font-normal"
                    >
                      <ReactTags
                        id="keywords"
                        allowNew
                        selected={projectData.keywordChips}
                        suggestions={keywordSuggestions}
                        onDelete={onKeywordTagDelete}
                        onAdd={onKeywordTagAddition}
                        placeholderText={"Add Keyword"}
                      />
                    </div>
                    {simpleValidator.current.message(
                      "Project Keywords",
                      projectData.keywordChips,
                      "required|array",
                      {
                        className: "text-red-600",
                      }
                    )}
                  </div>
                </div>

                {/* Project Area/Region section ------------------------------------------------------------- */}
                <div className="sm:col-span-6">
                  <label className="block text-base font-semibold text-gray-900 dark:text-gray-100">
                    Project Area/Region
                    <span className="text-red-700 font-bold border-red-200">
                      *
                    </span>
                  </label>
                  <div className="mt-1">
                    <div className="relative">
                      <MapContainer
                        center={[69.116864, -105.059681]}
                        zoom={3}
                        minZoom={2}
                        style={{ height: "300px", zIndex: 0 }}
                        worldCopyJump={true}
                        maxBoundsViscosity={1}
                        attributionControl={false}
                      >
                        <AreaSelect
                          isSelecting={isSelecting}
                          onBoundsUpdate={handleBoundsUpdate}
                          bounds={[
                            [
                              projectData.bounds.north || "",
                              projectData.bounds.east || "",
                            ],
                            [
                              projectData.bounds.south || "",
                              projectData.bounds.west || "",
                            ],
                          ]}
                        />
                      </MapContainer>
                      <div className="absolute right-2 top-2">
                        <button
                          onClick={() => {
                            const hasBounds = projectData.bounds.north !== "" && projectData.bounds.east !== "";
                            if (!hasBounds) {
                              setIsSelecting(true);
                            } else if (isSelecting) {
                              setIsSelecting(false);
                            }
                          }}
                          type="button"
                          disabled={projectData.bounds.north !== "" && projectData.bounds.east !== "" && !isSelecting}
                          className={`w-full justify-center content-center items-center px-3 py-2 center border text-sm leading-4 font-medium rounded-md shadow-sm ${
                            projectData.bounds.north !== "" && projectData.bounds.east !== "" && !isSelecting
                              ? "border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed"
                              : "border-gray-400 text-black bg-white hover:bg-gray-100 focus:outline-none"
                          }`}
                        >
                          {isSelecting ? "Done Selecting" : "Select Area"}
                        </button>
                      </div>
                    </div>

                    <div className="mt-2 text-right">
                      <button
                        onClick={(e) => onClickResetDirection(e)}
                        type="button"
                        className="w-full justify-center content-center items-center px-3 py-2 center border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-gray-300 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* LAT & LONG FIELDS ------------------------------------------------*/}
                  <div className="grid grid-cols-2 gap-4 w-full py-3 mt-2">
                    <div className="flex items-end justify-start">
                      <p className="bottom-0 mb-0 font-semibold text-sm dark:text-gray-100">
                        Latitude
                      </p>
                    </div>
                    <div className="flex items-end justify-start">
                      <p className="bottom-0 mb-0 font-semibold text-sm dark:text-gray-100">
                        Longitude
                      </p>
                    </div>

                    {/* NORTH FIELD ------------------------------------------------ */}
                    <div>
                      <label htmlFor="northLat" className="sr-only">
                        North Latitude
                      </label>
                      <input
                        id="northLat"
                        className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                        type="number"
                        placeholder="North"
                        value={projectData.bounds.north || ""}
                        size={10}
                        data-cy="northLat"
                        onChange={(e) => {
                          const bounds = {
                            ...projectData.bounds,
                            north: e.target.value,
                          };
                          handleCoordinateChange(bounds);
                        }}
                        onBlur={() => {
                          setUpdateBounds(true);
                        }}
                      />

                      {simpleValidator.current.message(
                        "North Bounds",
                        projectData.bounds.north,
                        "required",
                        {
                          className: "text-red-600",
                        }
                      )}
                    </div>

                    {/* EAST FIELD ------------------------------------------------ */}
                    <div>
                      <label htmlFor="eastLong" className="sr-only">
                        East Longitude
                      </label>
                      <input
                        id="eastLong"
                        className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                        type="number"
                        placeholder="East"
                        value={projectData.bounds.east || ""}
                        size={10}
                        data-cy="eastLong"
                        onChange={(e) => {
                          const bounds = {
                            ...projectData.bounds,
                            east: e.target.value,
                          };
                          handleCoordinateChange(bounds);
                        }}
                        onBlur={() => {
                          setUpdateBounds(true);
                        }}
                      />

                      {simpleValidator.current.message(
                        "East Bounds",
                        projectData.bounds.east,
                        "required",
                        {
                          className: "text-red-600",
                        }
                      )}
                    </div>

                    {/* SOUTH FIELD ------------------------------------------------ */}
                    <div>
                      <label htmlFor="southLat" className="sr-only">
                        South Latitude
                      </label>
                      <input
                        id="southLat"
                        className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                        type="number"
                        placeholder="South"
                        value={projectData.bounds.south || ""}
                        size={10}
                        data-cy="southLat"
                        onChange={(e) => {
                          const bounds = {
                            ...projectData.bounds,
                            south: e.target.value,
                          };
                          handleCoordinateChange(bounds);
                        }}
                        onBlur={() => {
                          setUpdateBounds(true);
                        }}
                      />

                      {simpleValidator.current.message(
                        "South Bounds",
                        projectData.bounds.south,
                        "required",
                        {
                          className: "text-red-600",
                        }
                      )}
                    </div>

                    {/* WEST FIELD ------------------------------------------------ */}
                    <div>
                      <label htmlFor="westLong" className="sr-only">
                        West Longitude
                      </label>
                      <input
                        id="westLong"
                        className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                        type="number"
                        placeholder="West"
                        value={projectData.bounds.west || ""}
                        size={10}
                        data-cy="westLong"
                        onChange={(e) => {
                          const bounds = {
                            ...projectData.bounds,
                            west: e.target.value,
                          };
                          handleCoordinateChange(bounds);
                        }}
                        onBlur={() => {
                          setUpdateBounds(true);
                        }}
                      />

                      {simpleValidator.current.message(
                        "West Bounds",
                        projectData.bounds.west,
                        "required",
                        {
                          className: "text-red-600",
                        }
                      )}
                    </div>
                  </div>
                </div>

                {/* Project Status section ------------------------------------------------------------- */}
                <div className="sm:col-span-6">
                  <label className="block text-base font-semibold text-gray-900 dark:text-gray-100">
                    Project Status
                    <span className="text-red-700 font-bold border-red-200">
                      *
                    </span>
                  </label>
                  <div className="mt-1 shadow-sm " data-cy="project-status">
                    <Select
                      className="placeholder:text-gray-900 "
                      value={projectData.selectedOption}
                      onChange={(selectedOption) =>
                        setProjectData((prevState) => ({
                          ...prevState,
                          selectedOption: selectedOption,
                        }))
                      }
                      options={options}
                      placeholder="Choose an item"
                    />
                  </div>

                  {simpleValidator.current.message(
                    "Project Status",
                    projectData.selectedOption,
                    "required",
                    {
                      className: "text-red-600",
                    }
                  )}
                </div>

                {/* Project Start Date section ------------------------------------------------------------- */}
                <span className="sm:col-span-3">
                  <label
                    htmlFor="startDate"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Project Start Date
                    <span className="text-red-700 font-bold border-red-200">
                      *
                    </span>
                  </label>
                  <div className="mt-1 text-sm text-gray-500">
                    <div className="mt-1">
                      <input
                        type="date"
                        name="date"
                        id="startDate"
                        value={projectData.selectedStartDate}
                        data-cy="start-date"
                        /* onChange={handleDayChange} */
                        onChange={(e) => handleChange(e, "selectedStartDate")}
                        max={projectData.selectedEndDate}
                        className="py-3 px-4 flex-1 block w-full focus:ring-1 outline-none focus:ring-blue-500 focus:border-blue-500 min-w-0 rounded-md shadow-sm border border-gray-300 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                      />
                    </div>

                    {simpleValidator.current.message(
                      "Project Start Date",
                      projectData.selectedStartDate,
                      "required",
                      {
                        className: "text-red-600",
                      }
                    )}
                  </div>
                </span>

                {/*  Project End Date section ------------------------------------------------------------- */}
                <span className="sm:col-span-3">
                  <label
                    htmlFor="endDate"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Project End Date
                    {projectData.selectedOption &&
                      projectData.selectedOption.value !== 1 && (
                        <span className="text-red-700 font-bold border-red-200">
                          *
                        </span>
                      )}
                  </label>
                  <div className="mt-1 text-sm text-gray-500">
                    <div className="mt-1">
                      <input
                        type="date"
                        name="date"
                        id="endDate"
                        value={projectData.selectedEndDate}
                        placeholder="TBD"
                        data-cy="end-date"
                        /* onChange={handleEndDayChange} */
                        onChange={(e) => handleChange(e, "selectedEndDate")}
                        min={projectData.selectedStartDate}
                        className="py-3 px-4 flex-1 block w-full focus:ring-1 outline-none focus:ring-blue-500 focus:border-blue-500 min-w-0 rounded-md shadow-sm border border-gray-300 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                      />
                    </div>

                    {simpleValidator.current.message(
                      "Project End Date",
                      projectData.selectedEndDate,
                      "endDateRequiredUnlessInProgress",
                      {
                        className: "text-red-600",
                      }
                    )}
                  </div>
                </span>

                {/* Project DOI section ------------------------------------------------------------- */}
                {/*
                <div className="sm:col-span-6">
                  <label
                    htmlFor="doi"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Project DOI
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="10.1234/foo"
                      onChange={(e) => handleChange(e, "doi")}
                      defaultValue={projectData.doi}
                      type="text"
                      name="doi"
                      id="doi"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                    {projectData.doi === "" ? (
                      <></>
                    ) : (
                      simpleValidator.current.message(
                        "Project doi",
                        projectData.doi,
                        "string",
                        {
                          className: "text-red-600",
                        }
                      )
                    )}
                  </div>
                </div>
                */}

                {/* Project Citation section ------------------------------------------------------------- */}
                <div className="sm:col-span-6">
                  <label
                    htmlFor="citation"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Project Citation
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Author1; Author2, Publication Date, Dataset Title, DOI, Data Repository, Version"
                      onChange={(e) => handleChange(e, "citation")}
                      defaultValue={projectData.citation}
                      type="text"
                      name="citation"
                      id="citation"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                {/* Vessels/Labs/Infrastructure Used section ------------------------------------------------------------- */}
                <div className="sm:col-span-6">
                  <label className="block text-base font-semibold text-gray-900 dark:text-gray-100">
                    Vessels/Labs/Infrastructure Used
                    <span className="text-red-700 font-bold border-red-200">
                      *
                    </span>
                  </label>
                  <div className="mt-1">
                    <div
                      data-cy="vessels"
                      className="block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md text-gray-800 font-normal"
                    >
                      <ReactTags
                        allowNew
                        selected={projectData.vesselChips}
                        suggestions={vesselSuggestions}
                        onDelete={onVesselTagDelete}
                        onAdd={onVesselTagAddition}
                        placeholderText={"Add Vessel"}
                      />
                    </div>
                  </div>
                  {simpleValidator.current.message(
                    "Vessels/Labs/Infrastructure",
                    projectData.vessels,
                    "required|array",
                    {
                      className: "text-red-600",
                    }
                  )}
                </div>

                <div className="sm:col-span-6">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="analyzedForNAVWARN"
                      className="text-base font-semibold text-gray-900 dark:text-gray-100"
                    >
                      Analyzed For NAVWARN
                    </label>
                    <input
                      type="checkbox"
                      name="analyzedForNAVWARN"
                      id="analyzedForNAVWARN"
                      checked={projectData.analyzedForNAVWARN}
                      onChange={handleAnalyzedForNAVWARNChange}
                      className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="backscatterAvailable"
                      className="text-base font-semibold text-gray-900 dark:text-gray-100"
                    >
                      Backscatter Available
                    </label>
                    <input
                      type="checkbox"
                      name="backscatterAvailable"
                      id="backscatterAvailable"
                      checked={projectData.backscatterAvailable}
                      onChange={handleBackscatterChange}
                      className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="soundingHardwareModel"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Sounding Hardware Model
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Sounding hardware + Model # (e.g. Hydrobox, Kongsberg EM2040)"
                      onChange={handleSoundingChange}
                      defaultValue={projectData.soundingHardwareModel}
                      type="text"
                      name="soundingHardwareModel"
                      id="soundingHardwareModel"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="techniqueOfSounding"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Technique Of Sounding
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Technique of Sounding (e.g. multi-beam sonar, LiDAR)"
                      onChange={handleTechniqueOfSoundingChange}
                      defaultValue={projectData.techniqueOfSounding}
                      type="text"
                      name="techniqueOfSounding"
                      id="techniqueOfSounding"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="resolution"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Resolution
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Resolution (e.g. 0.5m, 5m x 5m matrix)"
                      onChange={handleResolutionChange}
                      defaultValue={projectData.resolution}
                      type="text"
                      name="resolution"
                      id="resolution"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="dataAcquisitionSoftwareVersion"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Data Acquisition Software Version
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Data acquisition software + Version (e.g. HYPACK, QINsy, SIS, ISAH)"
                      onChange={handleDataAcquisitionSoftwareVersionChange}
                      defaultValue={projectData.dataAcquisitionSoftwareVersion}
                      type="text"
                      name="dataAcquisitionSoftwareVersion"
                      id="dataAcquisitionSoftwareVersion"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="dataProcessingSoftwareVersion"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Data Processing Software Version
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Data processing software + Version (e.g. HYPACK, FLEDERMAUS, AutoCAD, HIPS, JRSondeW7)"
                      onChange={handleDataProcessingSoftwareVersionChange}
                      defaultValue={projectData.dataProcessingSoftwareVersion}
                      type="text"
                      name="dataProcessingSoftwareVersion"
                      id="dataProcessingSoftwareVersion"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="horizontalCoordinatesSystem"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Horizontal Coordinates System
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Horizontal coordinates system (e.g. Northing, Easting, DD, D-M-S)"
                      onChange={handleHorizontalCoordinatesSystemChange}
                      defaultValue={projectData.horizontalCoordinatesSystem}
                      type="text"
                      name="horizontalCoordinatesSystem"
                      id="horizontalCoordinatesSystem"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="horizontalDatum"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Horizontal Datum
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Horizontal coordinates system (e.g. Northing, Easting, DD, D-M-S)"
                      onChange={handleHorizontalDatumChange}
                      defaultValue={projectData.horizontalDatum}
                      type="text"
                      name="horizontalDatum"
                      id="horizontalDatum"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="projection"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Projection
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Projection (e.g. UTM zone 3)"
                      onChange={handleProjectionChange}
                      defaultValue={projectData.projection}
                      type="text"
                      name="projection"
                      id="projection"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="positioningMethod"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Positioning Method
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Positioning method (e.g. DGPS, RTK, PPK)"
                      onChange={handlePositioningMethodChange}
                      defaultValue={projectData.positioningMethod}
                      type="text"
                      name="positioningMethod"
                      id="positioningMethod"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="positioningHardware"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Positioning Hardware
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Positioning hardware (e.g. Trimble R7, Trisponder, POSMV)"
                      onChange={handlePositioningHardwareChange}
                      defaultValue={projectData.positioningHardware}
                      type="text"
                      name="positioningHardware"
                      id="positioningHardware"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="benchmarkReference"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Benchmark Reference
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Benchmark reference (e.g. 80k0559)"
                      onChange={handleBenchmarkReferenceChange}
                      defaultValue={projectData.benchmarkReference}
                      type="text"
                      name="benchmarkReference"
                      id="benchmarkReference"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="benchmarkCoordinates"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Benchmark Coordinates
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Benchmark coordinates (Northing, Easting or D-M-S)"
                      onChange={handleBenchmarkCoordinatesChange}
                      defaultValue={projectData.benchmarkCoordinates}
                      type="text"
                      name="benchmarkCoordinates"
                      id="benchmarkCoordinates"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="verticalReferenceSystem"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Vertical Reference System
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Vertical reference system (e.g. CD, CGVD28, IGLD)"
                      onChange={handleVerticalReferenceSystemChange}
                      defaultValue={projectData.verticalReferenceSystem}
                      type="text"
                      name="verticalReferenceSystem"
                      id="verticalReferenceSystem"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="benchmarkReferenceHeight"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Benchmark Reference Height
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Benchmark reference height"
                      onChange={handleBenchmarkReferenceHeightChange}
                      defaultValue={projectData.benchmarkReferenceHeight}
                      type="text"
                      name="benchmarkReferenceHeight"
                      id="benchmarkReferenceHeight"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="waterLevelReductionMethodFileApplied"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Water Level Reduction Method File Applied
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Water level reduction method + file applied (e.g. HyVsep, Tidal observation)"
                      onChange={handleWaterLevelReductionMethodFileAppliedChange}
                      defaultValue={projectData.waterLevelReductionMethodFileApplied}
                      type="text"
                      name="waterLevelReductionMethodFileApplied"
                      id="waterLevelReductionMethodFileApplied"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="tidalStationReferenceVerticalReductionModel"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                   Tidal Station Reference/Vertical Reduction Model
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Tidal station reference / Vertical reduction model (e.g. Toronto #13320)"
                      onChange={handleTidalStationReferenceVerticalReductionModelChange}
                      defaultValue={projectData.tidalStationReferenceVerticalReductionModel}
                      type="text"
                      name="tidalStationReferenceVerticalReductionModel"
                      id="tidalStationReferenceVerticalReductionModel"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="horizontalAccuracy"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                  Horizontal Accuracy
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Horizontal accuracy (e.g. Â± 1m, Â±5m)"
                      onChange={handleHorizontalAccuracyChange}
                      defaultValue={projectData.horizontalAccuracy}
                      type="text"
                      name="horizontalAccuracy"
                      id="horizontalAccuracy"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="verticalAccuracy"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                  Vertical Accuracy
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="Vertical accuracy (e.g. Â±0.15m, Â±0.50m, Â±1m)"
                      onChange={handleVerticalAccuracyChange}
                      defaultValue={projectData.verticalAccuracy}
                      type="text"
                      name="verticalAccuracy"
                      id="verticalAccuracy"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="soundingCorrectedForVesselDraft"
                      className="text-base font-semibold text-gray-900 dark:text-gray-100"
                    >
                      Sounding Corrected For Vessel Draft
                    </label>
                    <input
                      type="checkbox"
                      name="soundingCorrectedForVesselDraft"
                      id="soundingCorrectedForVesselDraft"
                      checked={projectData.soundingCorrectedForVesselDraft}
                      onChange={handleSoundingCorrectedForVesselDraftChange}
                      className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="calibrationData"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Calibration Data
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="calibrationData"
                      id="calibrationData"
                      value={projectData.calibrationData || ''}
                      onChange={handleCalibrationDataChange}
                      placeholder="Calibration details"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="IHO_CATZOC"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                  IHO_CATZOC
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="IHO CATZOC (e.g. CATZOC = A2)"
                      onChange={handle_IHO_CATZOC_Change}
                      defaultValue={projectData.IHO_CATZOC}
                      type="text"
                      name="IHO_CATZOC"
                      id="IHO_CATZOC"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label
                    htmlFor="IHOOrderOfSurvey"
                    className="block text-base font-semibold text-gray-900 dark:text-gray-100"
                  >
                  IHO Order Of Survey
                  </label>
                  <div className="mt-1">
                    <input
                      placeholder="IHO Order of Survey (e.g. Special, 1A)"
                      onChange={handle_IHOOrderOfSurvey_Change}
                      defaultValue={projectData.IHOOrderOfSurvey}
                      type="text"
                      name="IHOOrderOfSurvey"
                      id="IHOOrderOfSurvey"
                      className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                </div>

              </div>
            </div>
          </div>
        </form>

        <button
          type="submit"
          className="m-auto mb-16 flex justify-center py-4 px-16 border  rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          data-cy="next"
          onClick={(e) => next(e)}
        >
          Next
        </button>
      </div>

      <Footer />
    </>
  );
}
